import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EyeIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '../../../services/api';
import { TASK_TYPE_LABELS, ASSIGNMENT_STATUS_LABELS } from '@claimup/shared/constants/assignments';
import { EXPEDIENT_TYPE_LABELS } from '@claimup/shared/constants/expedient';
import CreateAssignmentModal from '../../../components/AssignmentsSection/components/CreateAssignmentModal';
import Pagination from '../../../components/Pagination';

/**
 * Expedientes en gestión que NO tienen ninguna tarea viva (pendiente o en
 * curso): la lista que hay detrás de la cifra «sin tarea» del panel.
 *
 * Es una vista aparte y no filas mezcladas en la lista de tareas a propósito:
 * aquella tiene una fila por TAREA, y la edición en bloque, el recuento y el
 * orden por fecha dejarían de tener sentido con filas que no son tareas.
 */

/** Params de la URL que entiende el endpoint. */
const QUERY_PARAMS = ['client_id', 'estado', 'deudor', 'importe_min', 'importe_max', 'type', 'page'];

const formatAmount = (amount, currency) => {
  if (typeof amount !== 'number') return '—';
  try {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: currency || 'EUR' }).format(amount);
  } catch {
    return `${amount} ${currency || ''}`.trim();
  }
};

const diasDesde = fecha => (fecha ? Math.floor((Date.now() - new Date(fecha).getTime()) / 86400000) : null);

const UltimaTarea = ({ ultima, createdAt }) => {
  if (!ultima) {
    const dias = diasDesde(createdAt);
    return (
      <>
        <div className='text-slate-700'>Nunca ha tenido</div>
        {dias != null && <div className='text-xs text-slate-500'>Creado hace {dias} d</div>}
      </>
    );
  }
  const dias = diasDesde(ultima.cerrada_at);
  return (
    <>
      <div className='text-slate-700'>{TASK_TYPE_LABELS[ultima.task_type] || ultima.task_type}</div>
      <div className='text-xs text-slate-500'>
        {(ASSIGNMENT_STATUS_LABELS[ultima.status] || ultima.status).toLowerCase()}
        {dias != null && ` hace ${dias} d`}
      </div>
    </>
  );
};

const ExpedientesSinTarea = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expedientes, setExpedientes] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_items: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = {};
    for (const key of QUERY_PARAMS) {
      const value = searchParams.get(key);
      if (value) params[key] = key === 'estado' ? value.split(',') : value;
    }

    setLoading(true);
    api
      .get('/assignments/kpis/expedients-without-task', { params, paramsSerializer: { indexes: null } })
      .then(({ data }) => {
        setExpedientes(data.data.expedientes || []);
        setPagination(data.data.pagination || {});
        setError(null);
      })
      .catch(err => setError(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [searchParams]);

  // Al crearle una tarea deja de pertenecer a esta lista.
  const handleCreated = id => {
    setExpedientes(prev => prev.filter(e => e._id !== id));
    setPagination(prev => ({ ...prev, total_items: Math.max((prev.total_items || 1) - 1, 0) }));
  };

  const handlePageChange = page => {
    const next = new URLSearchParams(searchParams);
    next.set('page', page);
    setSearchParams(next);
  };

  if (loading) {
    return (
      <div className='text-center py-8'>
        <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500'></div>
        <p className='text-slate-500 mt-2'>Cargando...</p>
      </div>
    );
  }
  if (error) return <p className='text-sm text-red-700 py-8 text-center'>{error}</p>;

  return (
    <>
      <p className='text-sm text-slate-600 mb-3'>
        <strong>{(pagination.total_items || 0).toLocaleString('es-ES')}</strong> expedientes sin ninguna tarea pendiente
        ni en curso
      </p>

      {expedientes.length === 0 ? (
        <div className='text-center py-8'>
          <p className='text-slate-500'>Ningún expediente sin tarea con estos filtros</p>
        </div>
      ) : (
        <div className='overflow-x-auto'>
          <table className='table-auto w-full'>
            <thead className='text-xs font-semibold uppercase text-slate-500 bg-slate-50 border-t border-b border-slate-200'>
              <tr>
                {['Deudor', 'Cliente', 'Estado exp.', 'Tipo'].map(t => (
                  <th key={t} className='px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap'>
                    <div className='font-semibold text-left'>{t}</div>
                  </th>
                ))}
                <th className='px-2 py-3 whitespace-nowrap'>
                  <div className='font-semibold text-right'>Importe</div>
                </th>
                <th className='px-2 py-3 whitespace-nowrap'>
                  <div className='font-semibold text-left'>Última tarea</div>
                </th>
                <th className='px-2 last:pr-5 py-3 whitespace-nowrap w-20'>{/* Acciones */}</th>
              </tr>
            </thead>
            <tbody className='text-sm divide-y divide-slate-200'>
              {expedientes.map(e => (
                <tr key={e._id} className='hover:bg-slate-50'>
                  <td className='px-2 first:pl-5 py-3 whitespace-nowrap'>
                    <div className='text-slate-800 font-medium'>
                      {e.deudor || <span className='text-slate-400'>—</span>}
                    </div>
                    {e.deudores_count > 1 && <div className='text-xs text-slate-500'>+{e.deudores_count - 1} más</div>}
                  </td>
                  <td className='px-2 py-3 whitespace-nowrap'>
                    <div className='text-slate-700'>
                      {e.cliente || e.nombre_comercial || <span className='text-slate-400'>—</span>}
                    </div>
                    {e.cliente && e.nombre_comercial && e.nombre_comercial !== e.cliente && (
                      <div className='text-xs text-slate-500'>{e.nombre_comercial}</div>
                    )}
                  </td>
                  <td className='px-2 py-3 whitespace-nowrap text-xs text-slate-700'>{e.estado || '—'}</td>
                  <td className='px-2 py-3 whitespace-nowrap text-xs text-slate-700'>
                    {EXPEDIENT_TYPE_LABELS[e.type] || e.type}
                  </td>
                  <td className='px-2 py-3 whitespace-nowrap text-right text-slate-800 font-medium'>
                    {formatAmount(e.importe_deuda, e.divisa)}
                  </td>
                  <td className='px-2 py-3 whitespace-nowrap'>
                    <UltimaTarea ultima={e.ultima_tarea} createdAt={e.createdAt} />
                  </td>
                  <td className='px-2 last:pr-5 py-3 whitespace-nowrap'>
                    <div className='flex items-center justify-end gap-1'>
                      <CreateAssignmentModal expedientId={e._id} onAssignmentCreated={() => handleCreated(e._id)} />
                      <Link
                        to={`/view/${e._id}`}
                        className='p-1.5 text-primary-500 hover:text-primary-600 transition-colors'
                        title='Ver expediente'
                      >
                        <EyeIcon className='w-5 h-5' />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination.total_pages > 1 && (
        <div className='mt-6 flex justify-center'>
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.total_pages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </>
  );
};

export default ExpedientesSinTarea;
